import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { ContextualTooltip } from './TutorialOverlay';
import { comparisonsAPI } from '../lib/api';
import { 
  Plus, Trash2, Play, Loader2, Move, X, Save,
  ZoomIn, ZoomOut, RotateCcw, AlertTriangle,
  Coins, Maximize2, Edit2, ChevronDown, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * GuidedViewComparison Component - Redesigned per feedback utente
 * 
 * Workflow:
 * 1. Utente vede le anteprime dei due disegni (zoom fit)
 * 2. Click su "Vecchia Revisione" → apre modal fullscreen con zoom fit
 *    - Dentro il modal c'è "Aggiungi Vista" per disegnare rettangoli
 *    - "Salva Modifiche" salva posizione e nome
 * 3. Click su "Nuova Revisione" → apre modal fullscreen
 *    - I rettangoli del disegno A appaiono qui con stesse dimensioni
 *    - L'utente può solo SPOSTARLI (non ridimensionare)
 * 4. Avvia comparazione quando le viste sono posizionate su entrambi
 */

// Fullscreen Editor for drawing/editing rectangles
const FullscreenRectEditor = ({
  isOpen,
  onClose,
  image,
  imageBase64,
  title,
  views,
  onSaveViews,
  side, // 'A' or 'B'
  isDrawingSide, // true if user can draw new rectangles (only for side A)
  sourceImage, // Image from side A (used for preview overlay on side B)
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // Local state for views being edited
  const [localViews, setLocalViews] = useState([]);
  const [selectedViewIndex, setSelectedViewIndex] = useState(null);
  const [editingName, setEditingName] = useState(null);
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [currentRect, setCurrentRect] = useState(null);
  
  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  
  // Zoom and pan
  const [zoom, setZoom] = useState(100);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  
  // Track unsaved changes
  const [hasChanges, setHasChanges] = useState(false);
  
  // Load views when opening
  useEffect(() => {
    if (isOpen) {
      // Deep copy views
      setLocalViews(JSON.parse(JSON.stringify(views)));
      setSelectedViewIndex(null);
      setHasChanges(false);
      
      // Reset zoom/pan
      setZoom(100);
      setPan({ x: 0, y: 0 });
    }
  }, [isOpen, views]);
  
  // Calculate initial zoom to fit all (zoom tutto)
  useEffect(() => {
    if (isOpen && image && containerRef.current) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      
      const scaleX = (containerWidth - 60) / image.width;
      const scaleY = (containerHeight - 60) / image.height;
      const fitScale = Math.min(scaleX, scaleY, 1) * 100;
      
      setZoom(Math.round(fitScale));
      setPan({ x: 0, y: 0 });
    }
  }, [isOpen, image]);
  
  // For side B: auto-position rectangles from A if they don't have rectB yet
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen && side === 'B') {
      const needsPositioning = localViews.some(v => v.rectA && !v.rectB);
      if (needsPositioning) {
        const updatedViews = localViews.map(v => {
          if (v.rectA && !v.rectB) {
            return { ...v, rectB: { ...v.rectA } }; // Copy position from A
          }
          return v;
        });
        setLocalViews(updatedViews);
        setHasChanges(true);
      }
    }
    // Using localViews.length instead of localViews to avoid infinite loops
  }, [isOpen, side, localViews.length]);
  
  // Draw canvas
  const drawCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !image) return;
    
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw dark background
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Calculate image position with zoom
    const scale = zoom / 100;
    const imgWidth = image.width * scale;
    const imgHeight = image.height * scale;
    const x = (canvas.width - imgWidth) / 2 + pan.x;
    const y = (canvas.height - imgHeight) / 2 + pan.y;
    
    // Draw image
    ctx.drawImage(image, x, y, imgWidth, imgHeight);
    
    // Draw existing rectangles
    localViews.forEach((view, index) => {
      const rect = side === 'A' ? view.rectA : view.rectB;
      if (!rect) return;
      
      const isSelected = index === selectedViewIndex;
      
      // Convert percentage to pixels
      const rx = x + rect.x * imgWidth;
      const ry = y + rect.y * imgHeight;
      const rw = rect.w * imgWidth;
      const rh = rect.h * imgHeight;
      
      // For side B: Draw the cropped portion from Drawing A with transparency
      // This helps the user align the view precisely on the new revision
      if (side === 'B' && sourceImage && view.rectA) {
        // Calculate source coordinates from Drawing A
        const srcX = view.rectA.x * sourceImage.width;
        const srcY = view.rectA.y * sourceImage.height;
        const srcW = view.rectA.w * sourceImage.width;
        const srcH = view.rectA.h * sourceImage.height;
        
        // Draw the cropped image from A with transparency
        ctx.save();
        ctx.globalAlpha = isSelected ? 0.7 : 0.5;
        ctx.drawImage(
          sourceImage,
          srcX, srcY, srcW, srcH,  // Source rectangle (from Drawing A)
          rx, ry, rw, rh           // Destination rectangle (on Drawing B)
        );
        ctx.globalAlpha = 1.0;
        ctx.restore();
      } else {
        // Default: Fill with semi-transparent color (for side A or if no source)
        ctx.fillStyle = isSelected ? 'rgba(139, 92, 246, 0.25)' : 'rgba(139, 92, 246, 0.15)';
        ctx.fillRect(rx, ry, rw, rh);
      }
      
      // Draw border
      ctx.strokeStyle = isSelected ? '#a855f7' : '#8b5cf6';
      ctx.lineWidth = isSelected ? 3 : 2;
      ctx.setLineDash(side === 'B' ? [5, 3] : []);
      ctx.strokeRect(rx, ry, rw, rh);
      ctx.setLineDash([]);
      
      // Draw label
      const labelText = view.name || `Vista ${index + 1}`;
      ctx.font = 'bold 14px Arial';
      const textWidth = ctx.measureText(labelText).width;
      
      ctx.fillStyle = isSelected ? '#a855f7' : '#8b5cf6';
      ctx.fillRect(rx, ry - 26, textWidth + 16, 26);
      
      ctx.fillStyle = '#ffffff';
      ctx.textBaseline = 'middle';
      ctx.fillText(labelText, rx + 8, ry - 13);
      
      // Draw resize handles if selected and on drawing side (A only)
      if (isSelected && isDrawingSide) {
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#8b5cf6';
        ctx.lineWidth = 2;
        
        const handles = [
          { hx: rx, hy: ry },
          { hx: rx + rw, hy: ry },
          { hx: rx, hy: ry + rh },
          { hx: rx + rw, hy: ry + rh }
        ];
        
        handles.forEach(h => {
          ctx.beginPath();
          ctx.rect(h.hx - 5, h.hy - 5, 10, 10);
          ctx.fill();
          ctx.stroke();
        });
      }
      
      // Draw move indicator for side B
      if (side === 'B' && isSelected) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.beginPath();
        ctx.arc(rx + rw/2, ry + rh/2, 24, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#8b5cf6';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Draw move arrows
        ctx.fillStyle = '#8b5cf6';
        ctx.font = 'bold 18px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('⇔', rx + rw/2, ry + rh/2);
        ctx.textAlign = 'left';
      }
    });
    
    // Draw current drawing rectangle (while drawing new one)
    if (currentRect && isDrawingSide) {
      const rx = x + currentRect.x * imgWidth;
      const ry = y + currentRect.y * imgHeight;
      const rw = currentRect.w * imgWidth;
      const rh = currentRect.h * imgHeight;
      
      ctx.strokeStyle = '#22c55e';
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);
      ctx.strokeRect(rx, ry, rw, rh);
      ctx.setLineDash([]);
      
      ctx.fillStyle = 'rgba(34, 197, 94, 0.2)';
      ctx.fillRect(rx, ry, rw, rh);
    }
  }, [image, sourceImage, zoom, pan, localViews, selectedViewIndex, currentRect, side, isDrawingSide]);
  
  // Redraw on state change
  useEffect(() => {
    drawCanvas();
  }, [drawCanvas]);
  
  // Resize observer
  useEffect(() => {
    if (!containerRef.current) return;
    
    const observer = new ResizeObserver(() => {
      drawCanvas();
    });
    
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [drawCanvas]);
  
  // Convert screen coords to image percentage
  const screenToImagePercent = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas || !image) return null;
    
    const rect = canvas.getBoundingClientRect();
    const canvasX = clientX - rect.left;
    const canvasY = clientY - rect.top;
    
    const scale = zoom / 100;
    const imgWidth = image.width * scale;
    const imgHeight = image.height * scale;
    
    const imgX = (canvas.width - imgWidth) / 2 + pan.x;
    const imgY = (canvas.height - imgHeight) / 2 + pan.y;
    
    const percentX = (canvasX - imgX) / imgWidth;
    const percentY = (canvasY - imgY) / imgHeight;
    
    return { x: percentX, y: percentY };
  };
  
  // Find if click is inside a rectangle
  const findRectAtPoint = (px, py) => {
    for (let i = localViews.length - 1; i >= 0; i--) {
      const rect = side === 'A' ? localViews[i].rectA : localViews[i].rectB;
      if (!rect) continue;
      
      if (px >= rect.x && px <= rect.x + rect.w && py >= rect.y && py <= rect.y + rect.h) {
        return i;
      }
    }
    return null;
  };
  
  // Mouse handlers
  const handleMouseDown = (e) => {
    const pos = screenToImagePercent(e.clientX, e.clientY);
    if (!pos) return;
    
    // Check if clicking on existing rectangle
    const rectIndex = findRectAtPoint(pos.x, pos.y);
    
    if (rectIndex !== null) {
      setSelectedViewIndex(rectIndex);
      const rect = side === 'A' ? localViews[rectIndex].rectA : localViews[rectIndex].rectB;
      
      // Start dragging
      setIsDragging(true);
      setDragOffset({
        x: pos.x - rect.x,
        y: pos.y - rect.y
      });
    } else if (isDrawingSide) {
      // Start drawing new rectangle (only on side A)
      setSelectedViewIndex(null);
      setIsDrawing(true);
      setDrawStart(pos);
      setCurrentRect({ x: pos.x, y: pos.y, w: 0, h: 0 });
    } else {
      // Start panning
      setSelectedViewIndex(null);
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };
  
  const handleMouseMove = (e) => {
    if (isDrawing && drawStart && isDrawingSide) {
      const pos = screenToImagePercent(e.clientX, e.clientY);
      if (!pos) return;
      
      const x = Math.min(drawStart.x, pos.x);
      const y = Math.min(drawStart.y, pos.y);
      const w = Math.abs(pos.x - drawStart.x);
      const h = Math.abs(pos.y - drawStart.y);
      
      setCurrentRect({ x, y, w, h });
    } else if (isDragging && selectedViewIndex !== null) {
      const pos = screenToImagePercent(e.clientX, e.clientY);
      if (!pos) return;
      
      const newViews = [...localViews];
      const rect = side === 'A' ? newViews[selectedViewIndex].rectA : newViews[selectedViewIndex].rectB;
      
      if (rect) {
        const newX = Math.max(0, Math.min(1 - rect.w, pos.x - dragOffset.x));
        const newY = Math.max(0, Math.min(1 - rect.h, pos.y - dragOffset.y));
        
        if (side === 'A') {
          newViews[selectedViewIndex].rectA = { ...rect, x: newX, y: newY };
        } else {
          newViews[selectedViewIndex].rectB = { ...rect, x: newX, y: newY };
        }
        
        setLocalViews(newViews);
        setHasChanges(true);
      }
    } else if (isPanning) {
      setPan({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };
  
  const handleMouseUp = () => {
    if (isDrawing && currentRect && currentRect.w > 0.02 && currentRect.h > 0.02) {
      // Add new view
      const newView = {
        id: Date.now(),
        name: `Vista ${localViews.length + 1}`,
        rectA: currentRect,
        rectB: null
      };
      setLocalViews([...localViews, newView]);
      setSelectedViewIndex(localViews.length);
      setHasChanges(true);
      toast.success('Vista aggiunta!');
    }
    
    setIsDrawing(false);
    setDrawStart(null);
    setCurrentRect(null);
    setIsDragging(false);
    setIsPanning(false);
  };
  
  // Wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -10 : 10;
    setZoom(z => Math.max(20, Math.min(300, z + delta)));
  };
  
  // Delete selected view
  const handleDeleteView = () => {
    if (selectedViewIndex !== null) {
      const newViews = localViews.filter((_, i) => i !== selectedViewIndex);
      setLocalViews(newViews);
      setSelectedViewIndex(null);
      setHasChanges(true);
      toast.success('Vista eliminata');
    }
  };
  
  // Rename view
  const handleRenameView = (newName) => {
    if (selectedViewIndex !== null && newName.trim()) {
      const newViews = [...localViews];
      newViews[selectedViewIndex].name = newName.trim();
      setLocalViews(newViews);
      setHasChanges(true);
    }
    setEditingName(null);
  };
  
  // Handle scale change for a view (only for side B)
  const handleScaleChange = useCallback((viewIndex, scaleValue) => {
    if (viewIndex === null) return;
    
    // Parse scale ratio - support formats like "1:2", "1.5:1", "2:1"
    const parts = scaleValue.split(':');
    if (parts.length !== 2) {
      toast.error('Formato scala non valido. Usa il formato A:B (es. 1:2)');
      return;
    }
    
    const scaleA = parseFloat(parts[0]);
    const scaleB = parseFloat(parts[1]);
    
    if (isNaN(scaleA) || isNaN(scaleB) || scaleA <= 0 || scaleB <= 0) {
      toast.error('Valori scala non validi. Inserisci numeri positivi.');
      return;
    }
    
    const scaleFactor = scaleB / scaleA;
    
    // Use functional update to avoid stale closure issues
    setLocalViews(prevViews => {
      if (viewIndex >= prevViews.length) return prevViews;
      
      const view = prevViews[viewIndex];
      if (!view.rectA || !view.rectB) {
        return prevViews;
      }
      
      const rectA = view.rectA;
      const rectB = view.rectB;
      
      // Calculate new dimensions based on original rectA size
      const newW = rectA.w * scaleFactor;
      const newH = rectA.h * scaleFactor;
      
      // Keep center position, adjust x/y for new dimensions
      const centerX = rectB.x + rectB.w / 2;
      const centerY = rectB.y + rectB.h / 2;
      
      const newRectB = {
        x: Math.max(0, Math.min(1 - newW, centerX - newW / 2)),
        y: Math.max(0, Math.min(1 - newH, centerY - newH / 2)),
        w: newW,
        h: newH
      };
      
      // Create completely new array and objects to ensure React detects changes
      const newViews = prevViews.map((v, i) => {
        if (i === viewIndex) {
          return {
            ...v,
            scale: scaleValue,
            rectB: newRectB
          };
        }
        return v;
      });
      
      return newViews;
    });
    
    setHasChanges(true);
    toast.success(`Scala applicata: ${scaleValue}`);
  }, []);
  
  // Fit to screen (zoom tutto)
  const handleFitToScreen = () => {
    if (!containerRef.current || !image) return;
    
    const container = containerRef.current;
    const scaleX = (container.clientWidth - 60) / image.width;
    const scaleY = (container.clientHeight - 60) / image.height;
    const fitScale = Math.min(scaleX, scaleY, 1) * 100;
    
    setZoom(Math.round(fitScale));
    setPan({ x: 0, y: 0 });
  };
  
  // Save and close
  const handleSave = () => {
    onSaveViews(localViews);
    setHasChanges(false);
    toast.success('Modifiche salvate!');
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/98 flex flex-col">
      {/* Header */}
      <div className="h-16 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-4">
        <div className="flex items-center gap-4">
          <h2 className="text-white font-semibold text-lg">{title}</h2>
          
          {/* Add View button - only for side A, prominent */}
          {isDrawingSide && (
            <Button 
              onClick={() => {
                setSelectedViewIndex(null);
                toast.info('Disegna un rettangolo sul disegno per definire una vista');
              }}
              className="bg-green-600 hover:bg-green-700 text-white font-medium gap-2 px-4"
              data-testid="add-view-btn"
            >
              <Plus className="w-5 h-5" />
              Aggiungi Vista
            </Button>
          )}
          
          {/* Selected view controls */}
          {selectedViewIndex !== null && (
            <>
              <div className="h-8 w-px bg-slate-600" />
              
              {editingName === selectedViewIndex ? (
                <Input
                  autoFocus
                  defaultValue={localViews[selectedViewIndex]?.name || ''}
                  className="w-44 h-9 bg-slate-700 border-slate-600 text-white"
                  onBlur={(e) => handleRenameView(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRenameView(e.target.value);
                    if (e.key === 'Escape') setEditingName(null);
                  }}
                />
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setEditingName(selectedViewIndex)}
                  className="text-slate-300 hover:text-white gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  {localViews[selectedViewIndex]?.name || `Vista ${selectedViewIndex + 1}`}
                </Button>
              )}
              
              {isDrawingSide && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleDeleteView}
                  className="text-red-400 hover:text-red-300 hover:bg-red-900/30 gap-1"
                >
                  <Trash2 className="w-4 h-4" />
                  Elimina
                </Button>
              )}
              
              {/* Scale control - only for side B */}
              {!isDrawingSide && selectedViewIndex !== null && (
                <div className="flex items-center gap-2 ml-4 bg-slate-700/50 rounded-lg px-3 py-1" data-testid="scale-control">
                  <span className="text-sm text-slate-400">Scala:</span>
                  <input
                    type="text"
                    data-testid="scale-input"
                    key={selectedViewIndex} // Reset input when view changes
                    defaultValue={localViews[selectedViewIndex]?.scale || '1:1'}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value.includes(':')) {
                        handleScaleChange(selectedViewIndex, value);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value.includes(':')) {
                          handleScaleChange(selectedViewIndex, value);
                        }
                      }
                    }}
                    placeholder="es. 1:2"
                    className="w-20 bg-slate-600 border border-slate-500 text-white text-sm rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                  <span className="text-xs text-slate-500">(A:B)</span>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Zoom controls */}
          <div className="flex items-center gap-1 bg-slate-700 rounded-lg px-2 py-1">
            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-300 hover:text-white" onClick={() => setZoom(z => Math.max(20, z - 20))}>
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-300 w-14 text-center">{zoom}%</span>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-300 hover:text-white" onClick={() => setZoom(z => Math.min(300, z + 20))}>
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-300 hover:text-white" onClick={handleFitToScreen} title="Zoom tutto">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Save button */}
          <Button
            onClick={handleSave}
            disabled={!hasChanges && localViews.length === views.length}
            className={`gap-2 ${hasChanges ? 'bg-green-600 hover:bg-green-700 animate-pulse' : 'bg-green-600 hover:bg-green-700'} text-white`}
            data-testid="save-views-btn"
          >
            <Save className="w-4 h-4" />
            Salva Modifiche
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onClose}
            className="text-slate-300 hover:text-white hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Canvas */}
      <div 
        ref={containerRef}
        className="flex-1 relative overflow-hidden"
      >
        <canvas
          ref={canvasRef}
          className={`w-full h-full ${isDrawingSide ? 'cursor-crosshair' : 'cursor-grab'}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        />
        
        {/* Instructions */}
        <div className="absolute bottom-4 left-4 bg-slate-800/95 backdrop-blur-sm text-slate-300 text-sm p-4 rounded-lg max-w-md shadow-lg">
          {isDrawingSide ? (
            <>
              <p className="font-medium text-white mb-2">Definisci le viste da confrontare:</p>
              <p><strong>Disegna</strong> un rettangolo per creare una nuova vista</p>
              <p><strong>Click</strong> su una vista per selezionarla e spostarla</p>
              <p><strong>Scroll</strong> per zoom</p>
            </>
          ) : (
            <>
              <p className="font-medium text-white mb-2">Posiziona le viste sul nuovo disegno:</p>
              <p><strong>Trascina</strong> le viste per posizionarle correttamente</p>
              <p>Le dimensioni restano identiche a quelle definite sul primo disegno</p>
            </>
          )}
        </div>
        
        {/* Views counter */}
        <div className="absolute top-4 left-4 bg-slate-800/95 backdrop-blur-sm text-white px-4 py-2 rounded-lg shadow-lg">
          <span className="font-medium">{localViews.length}</span> {localViews.length === 1 ? 'vista definita' : 'viste definite'}
        </div>
      </div>
    </div>
  );
};

// Main component
const GuidedViewComparison = ({ 
  comparison, 
  drawingAName, 
  drawingBName,
  onAnalyzeViews,
  analyzing,
  userCredits = 0,
  forceExpanded = false
}) => {
  // Views state
  const [views, setViews] = useState([]);
  const [savingViews, setSavingViews] = useState(false);
  const [loadingViews, setLoadingViews] = useState(false);
  
  // Accordion expanded state
  const [expanded, setExpanded] = useState(false);
  
  // Effect to force expand when prop changes
  useEffect(() => {
    if (forceExpanded) {
      setExpanded(true);
    }
  }, [forceExpanded]);
  
  // Fullscreen editor state
  const [editorOpen, setEditorOpen] = useState(null); // 'A' or 'B' or null
  
  // Images
  const [imageA, setImageA] = useState(null);
  const [imageB, setImageB] = useState(null);
  
  // Load images
  useEffect(() => {
    if (comparison?.drawing_a_base64) {
      const img = new Image();
      img.onload = () => setImageA(img);
      img.src = `data:image/png;base64,${comparison.drawing_a_base64}`;
    }
    if (comparison?.drawing_b_base64) {
      const img = new Image();
      img.onload = () => setImageB(img);
      img.src = `data:image/png;base64,${comparison.drawing_b_base64}`;
    }
  }, [comparison]);
  
  // Load saved views from database
  useEffect(() => {
    const loadSavedViews = async () => {
      if (!comparison?.id) return;
      
      setLoadingViews(true);
      try {
        const response = await comparisonsAPI.getGuidedViews(comparison.id);
        if (response.data?.views && response.data.views.length > 0) {
          setViews(response.data.views);
          console.log('[GuidedViews] Loaded saved views:', response.data.views.length);
        }
      } catch (error) {
        console.error('[GuidedViews] Error loading saved views:', error);
      } finally {
        setLoadingViews(false);
      }
    };
    
    loadSavedViews();
  }, [comparison?.id]);
  
  // Calculate stats
  const completeViews = views.filter(v => v.rectA && v.rectB);
  const viewsWithA = views.filter(v => v.rectA).length;
  const viewsWithB = views.filter(v => v.rectB).length;
  const estimatedCredits = completeViews.length * 2;
  const hasEnoughCredits = userCredits >= estimatedCredits;
  
  // Handle save from editor - saves to database
  const handleSaveViews = async (newViews) => {
    setViews(newViews);
    
    // Save to database
    setSavingViews(true);
    try {
      await comparisonsAPI.saveGuidedViews(comparison.id, newViews);
      console.log('[GuidedViews] Saved views to database:', newViews.length);
    } catch (error) {
      console.error('[GuidedViews] Error saving views:', error);
      toast.error('Errore nel salvataggio delle viste');
    } finally {
      setSavingViews(false);
    }
  };
  
  // Launch analysis
  const handleLaunchAnalysis = () => {
    if (completeViews.length === 0) {
      toast.error('Definisci almeno una vista completa (area su A e B)');
      return;
    }
    if (!hasEnoughCredits) {
      toast.error(`Crediti insufficienti. Necessari: ${estimatedCredits}, Disponibili: ${userCredits}`);
      return;
    }
    
    onAnalyzeViews(completeViews);
  };
  
  if (!comparison?.drawing_a_base64 || !comparison?.drawing_b_base64) {
    return null;
  }
  
  return (
    <>
      {/* Fullscreen Editor for A */}
      <FullscreenRectEditor
        isOpen={editorOpen === 'A'}
        onClose={() => setEditorOpen(null)}
        image={imageA}
        imageBase64={comparison.drawing_a_base64}
        title={`${drawingAName} - Definisci le viste da confrontare`}
        views={views}
        onSaveViews={handleSaveViews}
        side="A"
        isDrawingSide={true}
      />
      
      {/* Fullscreen Editor for B */}
      <FullscreenRectEditor
        isOpen={editorOpen === 'B'}
        onClose={() => setEditorOpen(null)}
        image={imageB}
        imageBase64={comparison.drawing_b_base64}
        title={`${drawingBName} - Posiziona le viste corrispondenti`}
        views={views}
        onSaveViews={handleSaveViews}
        side="B"
        isDrawingSide={false}
        sourceImage={imageA}
      />
      
      <Card className="mt-6 border-purple-200 bg-gradient-to-br from-white to-purple-50/30" data-testid="guided-view-comparison">
        <CardContent className="pt-5">
          {/* Accordion Header - clickable to expand/collapse */}
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full flex items-center justify-between text-left group"
            data-testid="guided-comparison-toggle"
          >
            <ContextualTooltip
              id="guided-intro"
              content="Usa questa modalità quando le viste sono state spostate tra revisioni."
              position="bottom"
            >
              <p className="text-slate-700">
                Devi confrontare solo una o più viste del disegno? Utilizza la <span className="text-purple-700 font-semibold">Comparazione Guidata per Vista</span>
              </p>
            </ContextualTooltip>
            <div className={`ml-3 p-2 rounded-full bg-purple-100 group-hover:bg-purple-200 transition-all ${expanded ? 'rotate-180' : ''}`}>
              <ChevronDown className="w-5 h-5 text-purple-600" />
            </div>
          </button>
          
          {/* Accordion Content - only visible when expanded */}
          {expanded && (
            <div className="mt-4 space-y-4 animate-in slide-in-from-top-2 duration-200">
              {/* Credits info with tooltip */}
              <ContextualTooltip
                id="guided-credits"
                content="Ogni vista confrontata utilizza 2 crediti AI. Definisci solo le aree necessarie."
                position="bottom"
              >
                <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                  <Coins className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span className="text-amber-800">
                    <strong>2 crediti AI</strong> per ogni vista confrontata
                  </span>
                  <span className="text-slate-500 ml-auto">
                    Crediti disponibili: <strong>{userCredits}</strong>
                  </span>
                </div>
              </ContextualTooltip>
              
              {/* Previews - clickable to open fullscreen */}
              <div className="grid grid-cols-2 gap-4">
            {/* Preview A */}
            <div 
              className="relative cursor-pointer group"
              onClick={() => setEditorOpen('A')}
              data-testid="preview-drawing-a"
            >
              <div className="text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                <span>{drawingAName}</span>
                {viewsWithA > 0 && (
                  <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                    {viewsWithA} {viewsWithA === 1 ? 'vista' : 'viste'}
                  </span>
                )}
              </div>
              <div className="relative bg-white border-2 border-slate-200 rounded-lg overflow-hidden aspect-[4/3] group-hover:border-purple-400 transition-all group-hover:shadow-lg">
                {imageA && (
                  <img 
                    src={`data:image/png;base64,${comparison.drawing_a_base64}`}
                    alt={drawingAName}
                    className="w-full h-full object-contain p-1"
                  />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-lg">
                    <Maximize2 className="w-5 h-5 text-purple-600" />
                    <span className="font-medium text-purple-700">Clicca per definire le viste</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Preview B */}
            <div 
              className={`relative ${viewsWithA > 0 ? 'cursor-pointer group' : 'opacity-50 cursor-not-allowed'}`}
              onClick={() => viewsWithA > 0 && setEditorOpen('B')}
              data-testid="preview-drawing-b"
            >
              <div className="text-sm font-medium text-slate-700 mb-2 flex items-center justify-between">
                <span>{drawingBName}</span>
                {viewsWithB > 0 && (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                    {viewsWithB} {viewsWithB === 1 ? 'posizionata' : 'posizionate'}
                  </span>
                )}
              </div>
              <div className="relative bg-white border-2 border-slate-200 rounded-lg overflow-hidden aspect-[4/3] group-hover:border-green-400 transition-all group-hover:shadow-lg">
                {imageB && (
                  <img 
                    src={`data:image/png;base64,${comparison.drawing_b_base64}`}
                    alt={drawingBName}
                    className="w-full h-full object-contain p-1"
                  />
                )}
                {viewsWithA > 0 ? (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white px-4 py-2.5 rounded-lg flex items-center gap-2 shadow-lg">
                      <Move className="w-5 h-5 text-green-600" />
                      <span className="font-medium text-green-700">Clicca per posizionare le viste</span>
                    </div>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-100/80">
                    <span className="text-sm text-slate-500 bg-white px-3 py-1.5 rounded-lg shadow">
                      Prima definisci le viste su {drawingAName}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Status */}
          {views.length > 0 && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Viste definite:</span>
                <span className="font-medium">{views.length}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Viste complete (A + B):</span>
                <span className={`font-medium ${completeViews.length > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                  {completeViews.length}
                </span>
              </div>
              
              {completeViews.length < views.length && (
                <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  {views.length - completeViews.length} {views.length - completeViews.length === 1 ? 'vista non ancora posizionata' : 'viste non ancora posizionate'} su {drawingBName}
                </div>
              )}
            </div>
          )}
          
          {/* Launch button */}
          {completeViews.length > 0 && (
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-100 to-blue-100 border border-purple-200 rounded-lg">
              <div>
                <p className="font-medium text-slate-800">
                  Pronto: {completeViews.length} {completeViews.length === 1 ? 'vista' : 'viste'}
                </p>
                <p className="text-sm text-slate-600">
                  Costo: <span className={`font-bold ${hasEnoughCredits ? 'text-purple-700' : 'text-red-600'}`}>{estimatedCredits} crediti AI</span>
                </p>
              </div>
              <Button
                onClick={handleLaunchAnalysis}
                disabled={!hasEnoughCredits || analyzing}
                className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white gap-2 px-6"
                data-testid="launch-guided-comparison-btn"
              >
                {analyzing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analisi in corso...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4" />
                    Avvia Comparazione
                  </>
                )}
              </Button>
            </div>
          )}
          
          {completeViews.length > 0 && !hasEnoughCredits && (
            <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              Crediti insufficienti. Necessari {estimatedCredits}, disponibili {userCredits}.
            </div>
          )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default GuidedViewComparison;
